import React from "react";
import { Navigate, useLocation } from "react-router-dom";

/**
 * =============================================================================
 * 🧭 FallbackRedirect.tsx — Canonical Tenant-Safe Redirect (CTO Standard)
 * =============================================================================
 *
 * 🔐 Purpose:
 * Resolve a safe tenant-aware redirect when the app lands on:
 *   - root "/"
 *   - legacy non-tenant paths ("/ddo/*", "/pm/*", etc.)
 *   - wildcard "*"
 *
 * 📌 Resolution order (LOCKED):
 *   1) tenantSlug in URL (ONLY if not a reserved role/system segment)
 *   2) sessionStorage.tenant_slug
 *   3) "default" as last resort
 *
 * 🚫 Hard Guards:
 *   - Role segments (ddo/pm/finance/management) are NEVER treated as tenant slugs
 *   - System routes (login, 403) are NEVER treated as tenant slugs
 *   - Prevents infinite loops like "/ddo/login" → "/ddo/login"
 *
 * =============================================================================
 */

type Props = {
  replace?: boolean;
};

const FallbackRedirect: React.FC<Props> = ({ replace = true }) => {
  const location = useLocation();

  // Extract first path segment safely
  const firstSegment = location.pathname.split("/").filter(Boolean)[0] ?? null;

  /**
   * 🚫 Reserved segments — NEVER valid tenant slugs
   * This list is SECURITY + ROUTING CRITICAL
   */
  const RESERVED_SEGMENTS = new Set([
    "ddo",
    "pm",
    "pc",
    "finance",
    "management",
    "applicant",
    "client",
    "login",
    "403",
  ]);

  const candidate =
    firstSegment && firstSegment.trim() ? firstSegment.trim() : null;

  const urlSlug =
    candidate && !RESERVED_SEGMENTS.has(candidate) ? candidate : null;

  const storedSlugRaw = sessionStorage.getItem("tenant_slug");
  const storedSlug =
    storedSlugRaw && storedSlugRaw.trim() ? storedSlugRaw.trim() : null;

  const slug = urlSlug ?? storedSlug ?? "default";

  return <Navigate to={`/${slug}/login`} replace={replace} />;
};

export default FallbackRedirect;
